/**
 * SmartLayer Bridge — clean async interface for UI components.
 *
 * All heavy computation runs off the main thread via Web Worker.
 */

import { callSmartLayer } from '../utils/smartLayerWorker';

/* ── Settings helpers ── */

const SETTINGS_KEY = 'amorlia_smartlayer_settings';

const DEFAULT_SETTINGS = {
  platform: 'ebay',
  targetMargin: 0.42,
  platformFeeEstimate: 0.14,
  paymentFeeEstimate: 0.035,
  adBufferRate: 0.12,
  minProfitDollars: 5,
  minPublishScore: 78,
  strictCompliance: true,
};

export function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { ...DEFAULT_SETTINGS, settingsProfile: 'balanced' };
}

export function saveSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
}

export function getOrInitSettings(overrides = {}) {
  const stored = loadSettings();
  if (stored && stored.platform) return { ...stored, ...overrides };
  const fresh = { ...DEFAULT_SETTINGS, settingsProfile: 'balanced', ...overrides };
  saveSettings(fresh);
  return fresh;
}

/* ── Core operations (all async, run in worker) ── */

export async function generateProductIntelligence(product, settings) {
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  return callSmartLayer('generateSmartLayerUltimateFinalSafe', normalized, settings || loadSettings());
}

export async function buildCard(product, settings) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  try {
    const data = await callSmartLayer('buildSmartLayerDashboardCardV2', normalized, s);
    return { ok: true, label: 'buildCard', data };
  } catch (e) {
    return { ok: false, label: 'buildCard', error: e.message };
  }
}

export async function buildDetail(product, settings) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  try {
    const data = await callSmartLayer('buildSmartLayerProductDetailPagePack', normalized, s);
    return { ok: true, label: 'buildDetail', data };
  } catch (e) {
    return { ok: false, label: 'buildDetail', error: e.message };
  }
}

export async function precheck(product, settings) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  try {
    const data = await callSmartLayer('runSmartLayerFastPrecheck', normalized, s);
    return { ok: true, label: 'precheck', data };
  } catch (e) {
    return { ok: false, label: 'precheck', error: e.message };
  }
}

export async function getPopupResponse(product, settings) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  try {
    const data = await callSmartLayer('buildSmartLayerImporterPopupResponse', normalized, s);
    return { ok: true, label: 'popupResponse', data };
  } catch (e) {
    return { ok: false, label: 'popupResponse', error: e.message };
  }
}

export async function runSafeAction(actionId, product, settings, context = {}) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  return callSmartLayer('executeSmartLayerSafeAction', {
    role: 'ui',
    actionId,
    raw: normalized,
    userSettings: s,
    context,
  });
}

export async function runSelfTest(settings) {
  return callSmartLayer('runSmartLayerCurrentSelfTest', settings || loadSettings());
}

export async function runReadinessAudit(payload = {}) {
  return callSmartLayer('buildSmartLayerProductionReadinessAudit', payload);
}

export async function buildViewModel(items, settings, filters, sortBy) {
  const s = settings || loadSettings();
  const normalized = await Promise.all(
    (items || []).map(p => callSmartLayer('normalizeImportedProduct', p))
  );
  try {
    const data = await callSmartLayer('buildSmartLayerDashboardViewModelV2', {
      items: normalized,
      userSettings: s,
      filters,
      sortBy,
    });
    return { ok: true, label: 'viewModel', data };
  } catch (e) {
    return { ok: false, label: 'viewModel', error: e.message };
  }
}

export async function checkDraftGate(product, settings, completedChecks = {}) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  try {
    const data = await callSmartLayer('smartLayerFinalDraftGate', normalized, s, completedChecks);
    return { ok: true, label: 'draftGate', data };
  } catch (e) {
    return { ok: false, label: 'draftGate', error: e.message };
  }
}

export async function validateSettings(settings) {
  return callSmartLayer('validateSmartLayerSettings', settings || loadSettings());
}

/* ── Monitor helpers ── */

export async function buildMonitor(product, userMetrics, settings) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  try {
    const data = await callSmartLayer('buildSmartLayerLiveListingMonitor', {
      product: normalized,
      userMetrics,
      settings: s,
    });
    return { ok: true, label: 'monitor', data };
  } catch (e) {
    return { ok: false, label: 'monitor', error: e.message };
  }
}

export async function interpretMonitor(monitorResult) {
  try {
    const data = await callSmartLayer('interpretSmartLayerMonitor', monitorResult);
    return { ok: true, label: 'interpretMonitor', data };
  } catch (e) {
    return { ok: false, label: 'interpretMonitor', error: e.message };
  }
}

export async function buildExperiments(monitorResult, settings) {
  const s = settings || loadSettings();
  try {
    const data = await callSmartLayer('buildSmartLayerExperimentIdeas', monitorResult, s);
    return { ok: true, label: 'experiments', data };
  } catch (e) {
    return { ok: false, label: 'experiments', error: e.message };
  }
}

/* ── Order helpers ── */

export async function buildOrderCopyPack({ product, buyerAddress, selectedVariant, supplierUrl, settings }) {
  const s = settings || loadSettings();
  const normalized = await callSmartLayer('normalizeImportedProduct', product);
  try {
    const data = await callSmartLayer('buildSmartLayerOrderEntryCopyPack', {
      product: normalized,
      buyerAddress,
      selectedVariant,
      supplierUrl,
      settings: s,
    });
    return { ok: true, label: 'orderCopy', data };
  } catch (e) {
    return { ok: false, label: 'orderCopy', error: e.message };
  }
}
